import type {
  Comment,
  CommentEntityType,
  CommentsRepository,
  CommentThread,
  Id,
  Mentionable,
  WriteResult,
} from "@estelle/domain";

import { API_BASE, apiFetch } from "@/adapters/api/base";

/**
 * ERP-only comments, over HTTP (#448).
 *
 * The thread lives under the record it is on — `/api/orders/:id/comments`,
 * `/api/customers/:id/comments` — which is what makes the permission to read it
 * the permission to view the record. Nothing here reaches Shopify, and nothing
 * here keeps a copy of a comment: every answer is the server's, and a screen
 * replaces what it shows from the response, never from what it submitted.
 */

/** The path segment each kind of record's routes live under. */
const COLLECTION: Record<CommentEntityType, string> = {
  order: "orders",
  customer: "customers",
  escalation: "escalations",
};

function base(entityType: CommentEntityType, entityId: Id): string {
  return `/api/${COLLECTION[entityType]}/${encodeURIComponent(entityId)}/comments`;
}

async function read<T>(path: string): Promise<T> {
  const response = await apiFetch(path);
  if (!response.ok) {
    throw new Error(`Comments request failed: ${String(response.status)}`);
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. A 403 from
 * `requirePermission` is `{ error, permission }`, not a result, and becomes a
 * `forbidden` rejection naming what was missing.
 */
async function write(
  path: string,
  init: RequestInit,
): Promise<WriteResult<Comment>> {
  const response = await apiFetch(path, init);
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<Comment> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  if (response.status === 403) {
    const refusal = result as { permission?: string } | null;
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to do that.",
        permission: refusal?.permission ?? "comments.create",
      },
    };
  }
  throw new Error(
    `Comments ${init.method ?? "GET"} failed: ${String(response.status)}`,
  );
}

const jsonBody = (method: "POST" | "PATCH", body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

export function createApiComments(): CommentsRepository {
  return {
    list: (entityType, entityId) =>
      read<CommentThread>(base(entityType, entityId)),

    mentionable: (entityType, entityId) =>
      read<Mentionable[]>(`${base(entityType, entityId)}/people`),

    post: (entityType, entityId, input) => {
      const path = base(entityType, entityId);
      const files = input.files ?? [];
      // Text alone is JSON; with files it is a form, the one shape that can
      // carry bytes — the same split the API accepts.
      if (files.length === 0) {
        return write(path, jsonBody("POST", { body: input.body }));
      }
      const form = new FormData();
      form.append("body", input.body);
      for (const file of files) {
        form.append(
          "file",
          new Blob([file.data.slice()], { type: file.contentType }),
          file.filename,
        );
      }
      return write(path, { method: "POST", body: form });
    },

    edit: (entityType, entityId, commentId, body) =>
      write(
        `${base(entityType, entityId)}/${encodeURIComponent(commentId)}`,
        jsonBody("PATCH", { body }),
      ),

    delete: (entityType, entityId, commentId) =>
      write(`${base(entityType, entityId)}/${encodeURIComponent(commentId)}`, {
        method: "DELETE",
      }),

    // The download route, never the bucket: it is private, and every fetch of
    // this URL is permission-checked on the way through. Absolute, because the
    // API is a different origin from the SPA in every deployed environment.
    attachmentUrl: (entityType, entityId, attachmentId) =>
      `${API_BASE}${base(entityType, entityId)}/attachments/${encodeURIComponent(attachmentId)}`,
  };
}
